import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography,
  InputAdornment,
  IconButton,
  Stack,
  Alert
} from '@mui/material';
import { ColorLens as ColorIcon } from '@mui/icons-material';
import { HomeExpenseGroupCategoryDto } from '../../types/homeExpenses';

interface CategoryFormProps {
  initial?: HomeExpenseGroupCategoryDto;
  onSubmit: (data: {
    name: string;
    description?: string;
    color?: string;
    sortOrder: number;
  }) => void;
  onCancel?: () => void;
  loading?: boolean;
}

// Common colors for categories
const PRESET_COLORS = [
  '#f44336', // Red
  '#e91e63', // Pink
  '#9c27b0', // Purple
  '#673ab7', // Deep Purple
  '#3f51b5', // Indigo
  '#2196f3', // Blue
  '#03a9f4', // Light Blue
  '#00bcd4', // Cyan
  '#009688', // Teal
  '#4caf50', // Green
  '#8bc34a', // Light Green
  '#cddc39', // Lime
  '#ffeb3b', // Yellow
  '#ffc107', // Amber
  '#ff9800', // Orange
  '#ff5722', // Deep Orange
  '#795548', // Brown
  '#607d8b', // Blue Grey
];

const CategoryForm: React.FC<CategoryFormProps> = ({ 
  initial, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    color: initial?.color || PRESET_COLORS[0],
    sortOrder: initial?.sortOrder || 1,
  });
  
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = field === 'sortOrder' ? parseInt(event.target.value) || 0 : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleColorSelect = (color: string) => {
    setFormData(prev => ({ ...prev, color }));
    setShowColorPicker(false);
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'O nome da categoria é obrigatório';
    }
    
    if (formData.sortOrder < 0) {
      newErrors.sortOrder = 'A ordem tem de ser um número positivo';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    onSubmit({
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      color: formData.color,
      sortOrder: formData.sortOrder,
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        {initial ? 'Editar categoria' : 'Criar nova categoria'}
      </Typography>
      
      <Stack spacing={3}>
        <TextField
          label="Nome da categoria"
          value={formData.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
          required
          fullWidth
          disabled={loading}
          placeholder="ex.: Alimentação, Transportes, Lazer"
        />

        <TextField
          label="Descrição"
          value={formData.description}
          onChange={handleChange('description')}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          multiline
          rows={2}
          disabled={loading}
          placeholder="Descrição opcional desta categoria"
        />

        <TextField
          label="Ordem"
          type="number"
          value={formData.sortOrder}
          onChange={handleChange('sortOrder')}
          error={!!errors.sortOrder}
          helperText={errors.sortOrder || "Os números mais baixos aparecem primeiro nas listas"}
          required
          fullWidth
          disabled={loading}
          inputProps={{ min: 0, step: 1 }}
        />

        <Box>
          <TextField
            label="Cor"
            value={formData.color}
            onChange={handleChange('color')}
            fullWidth
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: formData.color,
                      border: '1px solid #ccc',
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    disabled={loading}
                  >
                    <ColorIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          {showColorPicker && (
            <Box sx={{ 
              mt: 1, 
              p: 2, 
              border: '1px solid #ccc', 
              borderRadius: 1, 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1 
            }}>
              {PRESET_COLORS.map((color) => (
                <Box
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'pointer',
                    border: formData.color === color ? '3px solid #000' : '1px solid #ccc',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {Object.keys(errors).length > 0 && (
          <Alert severity="error">
            Corrija os erros acima
          </Alert>
        )}

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && (
            <Button 
              onClick={onCancel} 
              disabled={loading}
              color="inherit"
            >
              Cancelar
            </Button>
          )}
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'A guardar...' : (initial ? 'Atualizar' : 'Criar')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CategoryForm;
